import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiDownload, FiUpload } from 'react-icons/fi';
import { MdEdit } from 'react-icons/md';
import { toast } from 'react-toastify';
import { SearchableSelect } from '../SearchableSelect';
import { useGetActiveComplaintLocationsQuery } from '../../../redux/complaintApi';
import { useGetProductCategoriesQuery } from '../../../redux/productCategoryApi';
import { useGetProductSubcategoriesQuery } from '../../../redux/productSubcategoryApi';
import { useGetProductsQuery } from '../../../redux/productApi';
import { useGetDailySalesQuery } from '../../../redux/dailySalesApi';
import { useImportMyToolCsvMutation } from '../../../redux/myToolsApi';
import {
  extractClosingStockRows,
  filenameFromDisposition,
  pickDraftId,
  closingStockQtyForProduct,
  useDestroyClosingStockDraftMutation,
} from '../../../redux/closingStockApi';
import { authFetch } from '../../../utils/authFetch';
import { API_BASE_URL } from '../../../utils/resolveAssetUrl';
import {
  defaultPlanningValue,
  filterCatalogProducts,
  itemPlanningValue,
  listFromApiResponse,
  mapApiCategory,
  mapApiProductToCatalogItem,
  mapApiSubcategory,
  noneSubKey,
  isNoneSubKey,
  withCategoryPlanning,
  withSubcategoryPlanning,
  itemSubcategoryKey,
} from '../../../utils/billToItemCatalog';
import {
  SALES_HISTORY_OPTIONS,
  salesHistoryWeeksApiValue,
  aggregateWeeklyItemHistory,
  deriveActualsFromWeeklyHistory,
  listDailySalesFromApi,
  mapApiDailySaleRow,
  weeklyRangeLabels,
} from '../../../utils/billToDailySales';
import { average, formatInt, formatMoney, toNumber } from '../../../utils/billToOrderCalc';
import HistoricalAverageSalesTable from './HistoricalAverageSalesTable';
import { FormulaInfoHint, ACTUAL_GC_SALES_FORMULAS } from './FormulaInfoHint';

const inputClasses =
  'mt-1 w-full rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

const actualGcCellClasses =
  'w-full rounded-lg border border-gray-200 bg-[#F7E5D5] px-2 py-1.5 text-sm cursor-default focus:border-gray-200 focus:outline-none focus:ring-0';

function locationId(loc) {
  return String(loc?.id ?? loc?.location_id ?? '').trim();
}

function locationName(loc) {
  return String(loc?.name ?? loc?.location_name ?? loc?.location ?? '').trim();
}

const EMPTY_LIST = [];
const EMPTY_PARAMS = {};

function asParam(value, digits) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  const p = 10 ** digits;
  return String(Math.round((n + Number.EPSILON) * p) / p);
}

function sanitizeNumeric(raw, allowNegative) {
  const value = String(raw ?? '');
  const pattern = allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;
  if (value === '' || pattern.test(value)) return value;
  return null;
}

function NumberTextField({ value, onChange, allowNegative, label, disabled }) {
  return (
    <label className="flex flex-col text-sm text-gray-700">
      <span>{label}</span>
      <input
        type="text"
        inputMode="decimal"
        value={value}
        placeholder="0"
        disabled={disabled}
        readOnly={disabled}
        onChange={(e) => {
          if (disabled) return;
          const next = sanitizeNumeric(e.target.value, allowNegative);
          if (next != null) onChange(next);
        }}
        className={`${inputClasses} ${
          disabled ? 'cursor-not-allowed bg-gray-100 text-gray-600 opacity-80' : ''
        }`}
      />
    </label>
  );
}

function PlanningPopup({
  title,
  subtitle,
  values,
  onChange,
  onClose,
  onSave,
  saving,
  variant = 'sku',
}) {
  const isSubcategory = variant === 'subcategory';
  const isSku = variant === 'sku';
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={saving ? undefined : onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h4 className="text-lg font-semibold text-gray-900">Planning Parameters</h4>
            <p className="mt-0.5 text-sm font-medium text-gray-800">{title}</p>
            {subtitle ? <p className="text-xs text-gray-500">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
            aria-label="Close"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="space-y-3">
          {isSku ? (
            <NumberTextField
              label="Closing Stock"
              value={values.closingStock ?? ''}
              onChange={() => {}}
              disabled
            />
          ) : null}
          {isSubcategory ? (
            <NumberTextField
              label="Lead Time (Days)"
              value={values.leadTime}
              onChange={(value) => onChange({ ...values, leadTime: value })}
            />
          ) : null}
          {/* Lead time is defined at subcategory level, not category/product.
          <NumberTextField
            label="Lead Time (Days)"
            value={values.leadTime}
            onChange={(value) => onChange({ ...values, leadTime: value })}
          />
          */}
          {!isSubcategory ? (
            <NumberTextField
              label="Buffer %"
              value={values.bufferPercent}
              onChange={(value) => onChange({ ...values, bufferPercent: value })}
            />
          ) : null}
          {!isSubcategory ? (
            <NumberTextField
              label="Projected Growth / Degrowth % (GC)"
              value={values.projectedGrowth}
              allowNegative
              onChange={(value) => onChange({ ...values, projectedGrowth: value })}
            />
          ) : null}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PlanningEditButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title="Edit"
      aria-label="Edit planning parameters"
      className="shrink-0 rounded-lg p-1.5 text-red-700 hover:bg-white"
    >
      <MdEdit className="h-5 w-5" />
    </button>
  );
}

function uniqueSortedIds(values) {
  return [...new Set((values || []).map((value) => String(value ?? '').trim()).filter(Boolean))]
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value))
    .sort((a, b) => a - b);
}

function labelsForCategories(categoryIds, catalogCategories, currentLabels = {}) {
  return (categoryIds || []).reduce((acc, id) => {
    acc[id] =
      catalogCategories.find((cat) => String(cat.id) === String(id))?.name ||
      currentLabels[id] ||
      id;
    return acc;
  }, {});
}

function saveBlobFile(blob, fileName) {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = fileName || 'closing_stock.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

function StepConfigureTool({ wizardData, onChange, onCsvImported }) {
  const [catSearch, setCatSearch] = useState('');
  const [subSearch, setSubSearch] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [planningEdit, setPlanningEdit] = useState(null);
  const csvFileInputRef = useRef(null);
  const { data: locationsRes, isLoading: locationsLoading } = useGetActiveComplaintLocationsQuery();
  // Product types API id (e.g. 1 Restaurant, 2 Showroom, 3 Mart)
  const productTypeIdRaw = String(wizardData.productTypeId || '').trim();
  const productTypeIdNum = Number(productTypeIdRaw);
  const productTypeId =
    productTypeIdRaw && Number.isFinite(productTypeIdNum) ? productTypeIdNum : null;
  const {
    data: categoriesRes,
    isLoading: categoriesLoading,
    isFetching: categoriesFetching,
  } = useGetProductCategoriesQuery(
    {
      status: true,
      per_page: 500,
      product_type_id: productTypeId,
    },
    { skip: productTypeId == null }
  );

  const params = wizardData.parameters || EMPTY_PARAMS;
  const hasOutlet = Boolean(String(wizardData.outletId || wizardData.outletName || '').trim());
  const hasDate = Boolean(String(wizardData.toolDate || '').trim());
  const hasWeeks = String(params.salesHistoryWeeks ?? '').trim() !== '';
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const [importMyToolCsv, { isLoading: isImportingCsv }] = useImportMyToolCsvMutation();
  const [destroyClosingStockDraft] = useDestroyClosingStockDraftMutation();

  const outletOptions = useMemo(() => {
    const list = locationsRes?.data || locationsRes || [];
    return (Array.isArray(list) ? list : [])
      .map((loc) => ({
        value: locationId(loc),
        label: locationName(loc) || locationId(loc),
      }))
      .filter((opt) => opt.value);
  }, [locationsRes]);

  // Location API count: 1 → auto-select, 2+ → user must choose from dropdown.
  useEffect(() => {
    if (locationsLoading) return;
    const currentId = String(wizardData.outletId || '').trim();

    if (outletOptions.length === 1) {
      const only = outletOptions[0];
      if (currentId === String(only.value) && wizardData.outletName === only.label) return;
      onChange({ outletId: only.value, outletName: only.label });
      return;
    }

    if (outletOptions.length > 1 && currentId) {
      const stillValid = outletOptions.some((opt) => String(opt.value) === currentId);
      if (!stillValid) {
        onChange({ outletId: '', outletName: '' });
      }
    }
  }, [
    locationsLoading,
    onChange,
    outletOptions,
    wizardData.outletId,
    wizardData.outletName,
  ]);

  const catalogCategories = useMemo(
    () => listFromApiResponse(categoriesRes).map(mapApiCategory).filter((cat) => cat.id),
    [categoriesRes]
  );

  useEffect(() => {
    if (!catalogCategories.length || wizardData.categoriesDefaulted) return;
    const nextCategories = catalogCategories.map((cat) => cat.id);
    onChange({
      categories: nextCategories,
      subCategories: [],
      selectedItemKeys: null,
      planningByCategory: withCategoryPlanning(
        wizardData.planningByCategory,
        nextCategories,
        catalogCategories
      ),
      categoryLabels: labelsForCategories(
        nextCategories,
        catalogCategories,
        wizardData.categoryLabels
      ),
      categoriesDefaulted: true,
    });
  }, [
    catalogCategories,
    onChange,
    wizardData.categoriesDefaulted,
    wizardData.planningByCategory,
    wizardData.categoryLabels,
  ]);

  const selectedCategories = Array.isArray(wizardData.categories)
    ? wizardData.categories.map(String)
    : EMPTY_LIST;
  const selectedSubs = Array.isArray(wizardData.subCategories)
    ? wizardData.subCategories
    : EMPTY_LIST;
  const selectedCategoryId =
    selectedCategories.length === 1 ? selectedCategories[0] : '';

  const {
    data: oneSubRes,
    isLoading: oneSubLoading,
    isFetching: oneSubFetching,
  } = useGetProductSubcategoriesQuery(
    {
      product_category_id: selectedCategoryId,
      status: true,
      per_page: 500,
    },
    { skip: selectedCategories.length !== 1 }
  );
  const {
    data: allSubRes,
    isLoading: allSubLoading,
    isFetching: allSubFetching,
  } = useGetProductSubcategoriesQuery(
    { status: true, per_page: 500 },
    { skip: selectedCategories.length < 2 }
  );
  const {
    data: productsRes,
    isLoading: productsLoading,
    isFetching: productsFetching,
  } = useGetProductsQuery(
    {
      status: true,
      per_page: 2000,
      product_type_id: productTypeId,
    },
    { skip: productTypeId == null }
  );

  const categoryNameById = useMemo(() => {
    const fromApi = Object.fromEntries(catalogCategories.map((cat) => [cat.id, cat.name]));
    return { ...(wizardData.categoryLabels || {}), ...fromApi };
  }, [catalogCategories, wizardData.categoryLabels]);

  const subcategoryRows = useMemo(() => {
    const selectedSet = new Set(selectedCategories);
    const raw =
      selectedCategories.length === 1
        ? listFromApiResponse(oneSubRes)
        : listFromApiResponse(allSubRes);
    return raw
      .map((row) => {
        const mapped = mapApiSubcategory(row, categoryNameById);
        if (mapped.categoryId) return mapped;
        if (selectedCategories.length !== 1) return mapped;
        const categoryId = selectedCategories[0];
        return {
          ...mapped,
          categoryId,
          id: `${categoryId}::${String(row.id ?? '')}`,
          categoryName: categoryNameById[categoryId] || mapped.categoryName,
        };
      })
      .filter((sub) => sub.id && selectedSet.has(sub.categoryId));
  }, [allSubRes, categoryNameById, oneSubRes, selectedCategories]);

  useEffect(() => {
    if (!subcategoryRows.length) return;
    const next = withSubcategoryPlanning(wizardData.planningBySubcategory, subcategoryRows);
    const prev = wizardData.planningBySubcategory || {};
    const unchanged = subcategoryRows.every((sub) => {
      return String(prev[sub.id]?.leadTime ?? '') === String(next[sub.id]?.leadTime ?? '');
    });
    if (unchanged && subcategoryRows.every((sub) => prev[sub.id])) return;
    onChange({ planningBySubcategory: next });
  }, [onChange, subcategoryRows, wizardData.planningBySubcategory]);

  const subcategoryNameById = useMemo(() => {
    const fromApi = {};
    subcategoryRows.forEach((sub) => {
      const compositeId = String(sub.id || '');
      const subId = compositeId.includes('::') ? compositeId.split('::')[1] : compositeId;
      if (subId) fromApi[subId] = sub.name;
      if (compositeId) fromApi[compositeId] = sub.name;
    });
    return fromApi;
  }, [subcategoryRows]);

  const filteredCategories = useMemo(() => {
    const q = catSearch.trim().toLowerCase();
    if (!q) return catalogCategories;
    return catalogCategories.filter((c) => c.name.toLowerCase().includes(q));
  }, [catSearch, catalogCategories]);

  const filteredSubs = useMemo(() => {
    const q = subSearch.trim().toLowerCase();
    if (!q) return subcategoryRows;
    return subcategoryRows.filter((sub) =>
      [sub.name, sub.categoryName].join(' ').toLowerCase().includes(q)
    );
  }, [subcategoryRows, subSearch]);

  const mappedProducts = useMemo(() => {
    return listFromApiResponse(productsRes).map((row) =>
      mapApiProductToCatalogItem(row, categoryNameById, subcategoryNameById)
    );
  }, [categoryNameById, productsRes, subcategoryNameById]);

  const catalogItems = useMemo(() => {
    const items = filterCatalogProducts(mappedProducts, selectedCategories, selectedSubs);
    const leadBySub = Object.fromEntries(
      subcategoryRows.map((sub) => [String(sub.id), sub.leadTime])
    );
    return items.map((item) => {
      const subcategoryKey = itemSubcategoryKey(item);
      return {
        ...item,
        subcategoryKey,
        subcategoryLeadTime: leadBySub[subcategoryKey] || '',
      };
    });
  }, [mappedProducts, selectedCategories, selectedSubs, subcategoryRows]);

  useEffect(() => {
    if (
      catalogItems.length === 0 &&
      Array.isArray(wizardData.productCatalog) &&
      wizardData.productCatalog.length > 0
    ) {
      return;
    }
    const prev = wizardData.productCatalog;
    const same =
      Array.isArray(prev) &&
      prev.length === catalogItems.length &&
      prev.every((item, index) => {
        const next = catalogItems[index];
        return (
          item.selectionKey === next?.selectionKey &&
          String(item.subCategory || '') === String(next?.subCategory || '') &&
          String(item.category || '') === String(next?.category || '') &&
          String(item.subcategoryId || '') === String(next?.subcategoryId || '') &&
          String(item.subcategoryLeadTime || '') === String(next?.subcategoryLeadTime || '')
        );
      });
    if (same) return;
    const skuPlanning = wizardData.planningBySku || {};
    onChange({
      productCatalog: catalogItems.map((item) => {
        const saved = skuPlanning[item.selectionKey];
        if (!saved) return item;
        return {
          ...item,
          bufferPercent: saved.bufferPercent ?? item.bufferPercent,
          projectedGrowth: saved.projectedGrowth ?? item.projectedGrowth,
        };
      }),
    });
  }, [catalogItems, onChange, wizardData.planningBySku, wizardData.productCatalog]);

  const selectedItemKeys = wizardData.selectedItemKeys;
  const allProductsSelected = selectedItemKeys == null;
  const selectedProductIdSet = useMemo(() => {
    const ids = new Set();
    if (!Array.isArray(selectedItemKeys)) return ids;
    const catalog = Array.isArray(wizardData.productCatalog) ? wizardData.productCatalog : [];
    catalog.forEach((item) => {
      if (!selectedItemKeys.includes(item.selectionKey)) return;
      const productId = String(item.productId || item.id || '').trim();
      const itemCode = String(item.itemCode || '').trim().toLowerCase();
      if (productId) ids.add(`id:${productId}`);
      if (itemCode) ids.add(`code:${itemCode}`);
    });
    return ids;
  }, [selectedItemKeys, wizardData.productCatalog]);
  const isDraftSelectedItem = useCallback((item) => {
    if (selectedItemKeys == null) return true;
    if (selectedItemKeys.includes(item.selectionKey)) return true;
    const productId = String(item.productId || item.id || '').trim();
    const itemCode = String(item.itemCode || '').trim().toLowerCase();
    return (
      (productId && selectedProductIdSet.has(`id:${productId}`)) ||
      (itemCode && selectedProductIdSet.has(`code:${itemCode}`))
    );
  }, [selectedItemKeys, selectedProductIdSet]);
  const selectedProductCount = allProductsSelected
    ? catalogItems.length
    : catalogItems.filter((item) => isDraftSelectedItem(item)).length;

  const selectedCatalogItems = useMemo(() => {
    if (selectedItemKeys == null) return catalogItems;
    return catalogItems.filter((item) => isDraftSelectedItem(item));
  }, [catalogItems, isDraftSelectedItem, selectedItemKeys]);

  const selectedProductIds = useMemo(
    () => uniqueSortedIds(selectedCatalogItems.map((item) => item.productId || item.id)),
    [selectedCatalogItems]
  );

  const closingStockRows = useMemo(
    () =>
      Array.isArray(wizardData.closingStockDraft?.rows)
        ? wizardData.closingStockDraft.rows
        : [],
    [wizardData.closingStockDraft]
  );

  useEffect(() => {
    const loc = String(wizardData.outletId || '').trim();
    const date = String(wizardData.toolDate || '').slice(0, 10);
    const current = wizardData.closingStockDraft;
    if (!current) return;
    const currentMatchesLocation =
      String(current?.locationId || '') === loc &&
      String(current?.date || '').slice(0, 10) === date;
    if (!currentMatchesLocation) {
      onChange({ closingStockDraft: null });
    }
  }, [onChange, wizardData.closingStockDraft, wizardData.outletId, wizardData.toolDate]);

  const handleDownloadClosingStock = async () => {
    if (productTypeId == null) {
      toast.error('Select an operating platform before downloading closing stock.');
      return;
    }
    if (!selectedProductIds.length) {
      toast.error('Select at least one SKU to download closing stock.');
      return;
    }
    const params = new URLSearchParams();
    params.set('product_type_id', String(productTypeId));
    selectedProductIds.forEach((id) => params.append('product_ids[]', String(id)));
    setIsDownloadingCsv(true);
    try {
      const res = await authFetch(
        `${API_BASE_URL}/api/my_tools/download_csv?${params.toString()}`,
        { credentials: 'omit' }
      );
      if (!res.ok) {
        let message = `Failed to download closing stock CSV (${res.status}).`;
        try {
          const json = await res.json();
          message = json?.message || json?.error || message;
        } catch {
          /* ignore non-JSON error body */
        }
        throw new Error(message);
      }
      const blob = await res.blob();
      saveBlobFile(blob, filenameFromDisposition(res.headers.get('content-disposition')));
      toast.success('Closing stock CSV downloaded.');
    } catch (err) {
      toast.error(err?.message || 'Failed to download closing stock CSV.');
    } finally {
      setIsDownloadingCsv(false);
    }
  };

  const handleUploadClosingStock = () => {
    if (productTypeId == null) {
      toast.error('Select an operating platform before uploading closing stock.');
      return;
    }
    if (!hasOutlet || !hasDate) {
      toast.error('Select outlet and date before uploading closing stock.');
      return;
    }
    if (!hasWeeks) {
      toast.error('Select sales history weeks before uploading closing stock.');
      return;
    }
    if (!selectedProductIds.length) {
      toast.error('Select at least one SKU to upload closing stock.');
      return;
    }
    csvFileInputRef.current?.click();
  };

  const handleClosingStockCsvChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a CSV file.');
      return;
    }
    if (productTypeId == null) {
      toast.error('Select an operating platform before uploading closing stock.');
      return;
    }
    const locationId = String(wizardData.outletId || '').trim();
    const date = String(wizardData.toolDate || '').slice(0, 10);
    const previousDraftId = String(wizardData.closingStockDraft?.draftId || '').trim();
    try {
      const result = await importMyToolCsv({
        file,
        product_type_id: productTypeId,
        location_id: locationId,
        date,
        sales_history_weeks: salesHistoryWeeksApiValue(params.salesHistoryWeeks),
        product_ids: selectedProductIds,
      }).unwrap();
      const nextId =
        pickDraftId(result?.data?.closing_stock) ||
        pickDraftId(result?.data) ||
        pickDraftId(result);
      const importedRows = extractClosingStockRows(result);
      const closingStockDraft = {
        draftId: nextId,
        locationId,
        date,
        fileName: file.name,
        rows: importedRows,
      };
      if (onCsvImported) {
        await onCsvImported({ closingStockDraft, importResult: result });
      } else {
        onChange({ closingStockDraft });
      }
      if (previousDraftId && nextId && previousDraftId !== nextId) {
        destroyClosingStockDraft(previousDraftId).catch(() => {});
      }
      toast.success(result?.message || result?.data?.message || 'Closing stock CSV uploaded.');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to upload closing stock CSV.');
    }
  };

  const dailySalesArgs = useMemo(() => {
    if (productTypeId == null || !hasWeeks || !wizardData.toolDate) return null;
    return {
      bill_date: wizardData.toolDate,
      page: 1,
      per_page: 2000,
      product_type_id: productTypeId,
    };
  }, [hasWeeks, productTypeId, wizardData.toolDate]);

  const {
    data: dailySalesRes,
    isLoading: dailySalesLoading,
    isFetching: dailySalesFetching,
    isError: dailySalesError,
  } = useGetDailySalesQuery(dailySalesArgs, { skip: !dailySalesArgs });

  const apiDailySalesRows = useMemo(
    () => listDailySalesFromApi(dailySalesRes).map(mapApiDailySaleRow).filter(Boolean),
    [dailySalesRes]
  );
  const dailySalesBusy = Boolean(dailySalesArgs) && (dailySalesLoading || dailySalesFetching);

  useEffect(() => {
    if (dailySalesBusy && apiDailySalesRows.length === 0) return;
    const prev = wizardData.dailySalesRows;
    const same =
      Array.isArray(prev) &&
      prev.length === apiDailySalesRows.length &&
      prev.every((row, index) => row.id === apiDailySalesRows[index]?.id);
    if (same) return;
    onChange({ dailySalesRows: apiDailySalesRows });
  }, [apiDailySalesRows, dailySalesBusy, onChange, wizardData.dailySalesRows]);

  const visibleItems = useMemo(() => {
    const q = itemSearch.trim().toLowerCase();
    if (!q) return catalogItems;
    return catalogItems.filter((item) =>
      [item.itemName, item.itemCode, item.subCategory, item.category]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [catalogItems, itemSearch]);

  const weekCount = Math.max(3, toNumber(params.salesHistoryWeeks, 3));
  const weeklyHistory = useMemo(
    () => (hasWeeks ? aggregateWeeklyItemHistory(apiDailySalesRows, weekCount) : new Map()),
    [apiDailySalesRows, hasWeeks, weekCount]
  );

  const derivedStoreActuals = useMemo(
    () => deriveActualsFromWeeklyHistory(selectedCatalogItems, weeklyHistory, weekCount),
    [selectedCatalogItems, weeklyHistory, weekCount]
  );
  const weekLabels = useMemo(
    () => weeklyRangeLabels(apiDailySalesRows, weekCount),
    [apiDailySalesRows, weekCount]
  );
  const derivedActuals = useMemo(() => {
    // API / history buckets are newest-first (w1 = closest week).
    // UI labels are chronological (W-1 oldest → W-3 newest), so reverse values.
    const sales = [...(derivedStoreActuals.sales || [])].reverse();
    const gc = [...(derivedStoreActuals.gc || [])].reverse();
    return {
      week1Gc: asParam(gc[0] || 0, 0),
      week2Gc: asParam(gc[1] || 0, 0),
      week3Gc: asParam(gc[2] || 0, 0),
      week1Sales: asParam(sales[0] || 0, 2),
      week2Sales: asParam(sales[1] || 0, 2),
      week3Sales: asParam(sales[2] || 0, 2),
      week1Label: weekLabels[0] || '',
      week2Label: weekLabels[1] || '',
      week3Label: weekLabels[2] || '',
    };
  }, [derivedStoreActuals, weekLabels]);
  const prevActualsRef = useRef('');

  useEffect(() => {
    if (!hasWeeks || dailySalesBusy) return;
    const signature = JSON.stringify(derivedActuals);
    if (prevActualsRef.current === signature) return;
    prevActualsRef.current = signature;
    onChange({ parameters: derivedActuals });
  }, [derivedActuals, dailySalesBusy, hasWeeks, onChange]);

  const avgGc = average([
    params.week1Gc || derivedActuals.week1Gc,
    params.week2Gc || derivedActuals.week2Gc,
    params.week3Gc || derivedActuals.week3Gc,
  ]);
  const avgSales = average([
    params.week1Sales || derivedActuals.week1Sales,
    params.week2Sales || derivedActuals.week2Sales,
    params.week3Sales || derivedActuals.week3Sales,
  ]);

  const isSubChecked = (sub) => {
    if (!selectedCategories.includes(sub.categoryId)) return false;
    const keysForSheet = selectedSubs.filter((key) => key.startsWith(`${sub.categoryId}::`));
    if (keysForSheet.some((key) => isNoneSubKey(key))) return false;
    if (!keysForSheet.length) return true;
    return keysForSheet.includes(sub.id);
  };

  const pruneItemKeys = (nextCategories, nextSubs) => {
    if (selectedItemKeys == null) return null;
    const eligible = filterCatalogProducts(mappedProducts, nextCategories, nextSubs).map(
      (item) => item.selectionKey
    );
    const eligibleSet = new Set(eligible);
    const kept = selectedItemKeys.filter((key) => eligibleSet.has(key));
    if (kept.length === eligible.length) return null;
    return kept;
  };

  const applyCategoryChange = (nextCategories, nextSubs) => {
    const nextSubLabels = (nextSubs || []).reduce((acc, key) => {
      const match = subcategoryRows.find((sub) => sub.id === key);
      acc[key] = match
        ? match.categoryName
          ? `${match.categoryName} · ${match.name}`
          : match.name
        : wizardData.subCategoryLabels?.[key] || key;
      return acc;
    }, {});
    onChange({
      categories: nextCategories,
      subCategories: nextSubs,
      selectedItemKeys: pruneItemKeys(nextCategories, nextSubs),
      planningByCategory: withCategoryPlanning(
        wizardData.planningByCategory,
        nextCategories,
        catalogCategories
      ),
      categoryLabels: labelsForCategories(
        nextCategories,
        catalogCategories,
        wizardData.categoryLabels
      ),
      subCategoryLabels: nextSubLabels,
    });
  };

  const toggleCategory = (cat) => {
    const checked = selectedCategories.includes(cat.id);
    const nextCategories = checked
      ? selectedCategories.filter((id) => id !== cat.id)
      : [...selectedCategories, cat.id];
    const nextSubs = selectedSubs.filter((key) => !key.startsWith(`${cat.id}::`));
    applyCategoryChange(nextCategories, nextSubs);
  };

  const savePlanningEdit = () => {
    if (!planningEdit) return;
    const { type, values } = planningEdit;

    if (type === 'category') {
      const cat = planningEdit.cat;
      if (!cat?.id) {
        toast.error('Category id is missing.');
        return;
      }
      const categoryId = String(cat.id);
      const catPlanning = {
        bufferPercent: values.bufferPercent,
        projectedGrowth: values.projectedGrowth,
      };
      const nextSku = { ...(wizardData.planningBySku || {}) };
      catalogItems.forEach((item) => {
        if (String(item.sheet) !== categoryId) return;
        nextSku[item.selectionKey] = {
          ...(nextSku[item.selectionKey] || {}),
          ...catPlanning,
        };
      });
      onChange({
        planningByCategory: {
          ...(wizardData.planningByCategory || {}),
          [cat.id]: {
            ...(wizardData.planningByCategory?.[cat.id] || {}),
            ...catPlanning,
            userEdited: true,
          },
        },
        planningBySku: nextSku,
        productCatalog: (wizardData.productCatalog || []).map((row) =>
          String(row.sheet) === categoryId ? { ...row, ...catPlanning } : row
        ),
      });
    } else if (type === 'subcategory') {
      const sub = planningEdit.sub;
      if (!sub?.id) {
        toast.error('Subcategory id is missing.');
        return;
      }
      onChange({
        planningBySubcategory: {
          ...(wizardData.planningBySubcategory || {}),
          [sub.id]: {
            ...(wizardData.planningBySubcategory?.[sub.id] || {}),
            leadTime: values.leadTime,
            userEdited: true,
          },
        },
      });
    } else if (type === 'sku') {
      const item = planningEdit.item;
      const productId = item?.productId || item?.id;
      if (!productId) {
        toast.error('SKU id is missing.');
        return;
      }
      const skuPlanning = {
        bufferPercent: values.bufferPercent,
        projectedGrowth: values.projectedGrowth,
      };
      const nextSku = { ...(wizardData.planningBySku || {}) };
      nextSku[item.selectionKey] = {
        ...(nextSku[item.selectionKey] || {}),
        ...skuPlanning,
      };
      onChange({
        planningBySku: nextSku,
        productCatalog: (wizardData.productCatalog || []).map((row) =>
          String(row.productId || row.id) === String(productId) ||
          row.selectionKey === item.selectionKey
            ? { ...row, ...skuPlanning }
            : row
        ),
      });
    }
    setPlanningEdit(null);
  };

  const openCategoryPlanning = (cat) => {
    setPlanningEdit({
      type: 'category',
      cat,
      title: cat.name,
      subtitle: 'Applies to all selected SKUs in this category',
      values: {
        bufferPercent:
          wizardData.planningByCategory?.[cat.id]?.bufferPercent ??
          cat.bufferPercent ??
          defaultPlanningValue('bufferPercent'),
        projectedGrowth:
          wizardData.planningByCategory?.[cat.id]?.projectedGrowth ??
          cat.projectedGrowth ??
          defaultPlanningValue('projectedGrowth'),
      },
    });
  };

  const openSubcategoryPlanning = (sub) => {
    setPlanningEdit({
      type: 'subcategory',
      sub,
      title: sub.name,
      subtitle: sub.categoryName
        ? `${sub.categoryName} · lead time for SKUs in this subcategory`
        : 'Lead time for SKUs in this subcategory',
      values: {
        leadTime:
          wizardData.planningBySubcategory?.[sub.id]?.leadTime ??
          sub.leadTime ??
          defaultPlanningValue('leadTime'),
      },
    });
  };

  const openSkuPlanning = (item) => {
    setPlanningEdit({
      type: 'sku',
      item,
      title: item.itemName,
      subtitle: `${item.itemCode} · ${item.subCategory || item.category}`,
      values: {
        closingStock: closingStockQtyForProduct(
          closingStockRows,
          item.productId || item.id,
          item.itemCode
        ),
        bufferPercent: itemPlanningValue(item, wizardData, 'bufferPercent'),
        projectedGrowth: itemPlanningValue(item, wizardData, 'projectedGrowth'),
      },
    });
  };

  const toggleSub = (sub) => {
    const cat = catalogCategories.find((row) => row.id === sub.categoryId);
    if (!cat) return;
    const allKeys = subcategoryRows
      .filter((row) => row.categoryId === cat.id)
      .map((row) => row.id);
    const keysForSheet = selectedSubs.filter((key) => key.startsWith(`${cat.id}::`));
    const hasNone = keysForSheet.some((key) => isNoneSubKey(key));
    const effective = hasNone ? [] : keysForSheet.length ? keysForSheet : allKeys;
    const nextSheet = effective.includes(sub.id)
      ? effective.filter((key) => key !== sub.id)
      : [...effective, sub.id];
    const other = selectedSubs.filter((key) => !key.startsWith(`${cat.id}::`));
    const nextCategories = selectedCategories.includes(cat.id)
      ? selectedCategories
      : [...selectedCategories, cat.id];

    // Keep category selected even when every subcategory is unchecked.
    if (!nextSheet.length) {
      applyCategoryChange(nextCategories, [...other, noneSubKey(cat.id)]);
      return;
    }

    const allSelectedForSheet = nextSheet.length === allKeys.length;
    const nextSubs = allSelectedForSheet ? other : [...other, ...nextSheet];
    applyCategoryChange(nextCategories, nextSubs);
  };

  const isProductChecked = (item) => isDraftSelectedItem(item);

  const toggleProduct = (item) => {
    const allKeys = catalogItems.map((row) => row.selectionKey);
    const current = selectedItemKeys == null ? allKeys : selectedItemKeys;
    const next = current.includes(item.selectionKey)
      ? current.filter((key) => key !== item.selectionKey)
      : [...current, item.selectionKey];
    onChange({
      selectedItemKeys: next.length === allKeys.length ? null : next,
    });
  };

  const toggleAllProducts = () => {
    if (catalogItems.length === 0) return;
    if (selectedProductCount === catalogItems.length) {
      onChange({ selectedItemKeys: [] });
      return;
    }
    onChange({ selectedItemKeys: null });
  };

  const allCategoriesSelected =
    catalogCategories.length > 0 &&
    catalogCategories.every((cat) => selectedCategories.includes(cat.id));

  const selectedSubCount = subcategoryRows.filter((sub) => isSubChecked(sub)).length;
  const allSubsSelected =
    subcategoryRows.length > 0 && selectedSubCount === subcategoryRows.length;

  const toggleAllCategories = () => {
    if (catalogCategories.length === 0) return;
    if (allCategoriesSelected) {
      applyCategoryChange([], []);
      return;
    }
    applyCategoryChange(
      catalogCategories.map((cat) => cat.id),
      []
    );
  };

  const toggleAllSubs = () => {
    if (subcategoryRows.length === 0) return;
    if (allSubsSelected) {
      // Deselect every subcategory, but keep parent categories selected.
      const catIds = [
        ...new Set(
          subcategoryRows
            .map((sub) => sub.categoryId)
            .filter((id) => selectedCategories.includes(id))
        ),
      ];
      const other = selectedSubs.filter(
        (key) => !catIds.some((id) => key.startsWith(`${id}::`))
      );
      applyCategoryChange(
        selectedCategories,
        [...other, ...catIds.map((id) => noneSubKey(id))]
      );
      return;
    }
    applyCategoryChange(selectedCategories, []);
  };

  const updateParam = (key, value) => {
    onChange({ parameters: { ...params, [key]: value } });
  };

  const handleOutletChange = (event) => {
    const nextId = String(event?.target?.value ?? '').trim();
    const match = outletOptions.find((opt) => String(opt.value) === nextId);
    onChange({
      outletId: nextId,
      outletName: match?.label || '',
    });
  };

  const categoriesBusy = categoriesLoading || categoriesFetching;
  const subsBusy =
    selectedCategories.length === 1
      ? oneSubLoading || oneSubFetching
      : selectedCategories.length > 1
        ? allSubLoading || allSubFetching
        : false;
  const productsBusy = productsLoading || productsFetching;

  return (
    <div className="min-w-0 space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Step 2: Configure Tool</h3>
        <p className="mt-1 text-sm text-gray-500">
          Select outlet, date, and sales history weeks first. Click the edit icon on a selected
          category or SKU to set lead time, buffer %, and GC growth.
        </p>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <h4 className="mb-4 text-sm font-semibold text-gray-800">Outlet & Period</h4>
        <div className="grid gap-4 sm:grid-cols-3">
          <label className="flex flex-col text-sm text-gray-700">
            <span>
              Outlet <span className="text-red-600">*</span>
            </span>
            <SearchableSelect
              name="outletId"
              value={wizardData.outletId || ''}
              onChange={handleOutletChange}
              options={outletOptions}
              placeholder={locationsLoading ? 'Loading outlets…' : 'Select outlet'}
              className={inputClasses}
            />
          </label>
          <label className="flex flex-col text-sm text-gray-700">
            <span>
              Date <span className="text-red-600">*</span>
            </span>
            <input
              type="date"
              value={wizardData.toolDate || ''}
              disabled={!hasOutlet}
              onChange={(e) => onChange({ toolDate: e.target.value })}
              onClick={(e) => {
                try {
                  e.currentTarget.showPicker?.();
                } catch {
                  /* ignore unsupported / already-open */
                }
              }}
              className={`${inputClasses} cursor-pointer disabled:cursor-not-allowed disabled:opacity-60`}
            />
          </label>
          <label className="flex flex-col text-sm text-gray-700">
            <span>
              Sales History (Weeks) <span className="text-red-600">*</span>
            </span>
            <select
              value={params.salesHistoryWeeks ?? ''}
              disabled={!hasDate}
              onChange={(e) => updateParam('salesHistoryWeeks', e.target.value)}
              className={`${inputClasses} disabled:cursor-not-allowed disabled:opacity-60`}
            >
              <option value="">Select weeks</option>
              {SALES_HISTORY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        {!hasOutlet ? (
          <p className="mt-3 text-sm text-amber-700">Select an outlet to continue.</p>
        ) : !hasDate ? (
          <p className="mt-3 text-sm text-amber-700">Select a date to continue.</p>
        ) : !hasWeeks ? (
          <p className="mt-3 text-sm text-amber-700">
            Select sales history weeks to continue with category, subcategory, and SKU.
          </p>
        ) : null}
      </div>

      <fieldset
        disabled={!hasWeeks}
        className={`min-w-0 ${!hasWeeks ? 'pointer-events-none opacity-50' : ''}`}
      >
        <div className="grid items-stretch gap-6 lg:grid-cols-3">
          <div className="flex h-full flex-col rounded-xl border border-gray-200 bg-white p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h4 className="text-sm font-semibold text-gray-800">
                Category ({selectedCategories.length}/{catalogCategories.length})
              </h4>
              {catalogCategories.length > 0 ? (
                <button
                  type="button"
                  onClick={toggleAllCategories}
                  className="shrink-0 text-xs font-medium text-brand hover:underline"
                >
                  {allCategoriesSelected ? 'Deselect All' : 'Select All'}
                </button>
              ) : null}
            </div>
                <input
                  type="text"
              placeholder="Search category type"
                  value={catSearch}
                  onChange={(e) => setCatSearch(e.target.value)}
              className="mb-3 w-full rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
            {categoriesBusy && catalogCategories.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500">Loading categories…</p>
            ) : filteredCategories.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500">
                {catSearch.trim() ? 'No categories match your search.' : 'No categories found.'}
              </p>
            ) : (
              <ul className="max-h-72 flex-1 space-y-1 overflow-y-auto">
                {filteredCategories.map((cat) => {
                  const checked = selectedCategories.includes(cat.id);
                  return (
                    <li key={cat.id} className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 hover:bg-gray-50">
                      <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 text-sm">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleCategory(cat)}
                          className="h-4 w-4 rounded border-gray-300 text-brand accent-brand"
                        />
                        <span className={`min-w-0 flex-1 truncate ${checked ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                          {cat.name}
                        </span>
                        {cat.items ? <span className="text-xs text-gray-400">{cat.items}</span> : null}
                      </label>
                      {checked ? (
                        <PlanningEditButton onClick={() => openCategoryPlanning(cat)} />
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
              </div>

          <div className="flex h-full flex-col rounded-xl border border-gray-200 bg-white p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h4 className="text-sm font-semibold text-gray-800">
                Sub Category ({selectedSubCount}/{subcategoryRows.length})
              </h4>
              {subcategoryRows.length > 0 ? (
              <button
                type="button"
                  onClick={toggleAllSubs}
                className="shrink-0 text-xs font-medium text-brand hover:underline"
              >
                  {allSubsSelected ? 'Deselect All' : 'Select All'}
              </button>
              ) : null}
            </div>
            <input
              type="text"
              placeholder="Search subcategory type"
              value={subSearch}
              onChange={(e) => setSubSearch(e.target.value)}
              className="mb-3 w-full rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
            {selectedCategories.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500">Select a category first.</p>
            ) : subsBusy && subcategoryRows.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500">Loading subcategories…</p>
            ) : filteredSubs.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500">
                {subSearch.trim()
                  ? 'No subcategories match your search.'
                  : 'No subcategories found.'}
              </p>
            ) : (
              <ul className="max-h-72 flex-1 space-y-1 overflow-y-auto">
                {filteredSubs.map((sub) => {
                  const checked = isSubChecked(sub);
                  return (
                    <li key={sub.id} className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 hover:bg-gray-50">
                      <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 text-sm">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSub(sub)}
                          className="h-4 w-4 rounded border-gray-300 text-brand accent-brand"
                        />
                        <span className={`min-w-0 flex-1 truncate ${checked ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                          {sub.name}
                        </span>
                        {sub.items ? <span className="text-xs text-gray-400">{sub.items}</span> : null}
                      </label>
                      {checked ? (
                        <PlanningEditButton onClick={() => openSubcategoryPlanning(sub)} />
                      ) : null}
                    </li>
                  );
                })}
                      </ul>
                    )}
          </div>

          <div className="flex h-full flex-col rounded-xl border border-gray-200 bg-white p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h4 className="text-sm font-semibold text-gray-800">
                SKU ({selectedProductCount}/{catalogItems.length})
              </h4>
              {catalogItems.length > 0 ? (
                <div className="flex shrink-0 items-center gap-1.5">
                  <input
                    ref={csvFileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    disabled={isImportingCsv}
                    onChange={handleClosingStockCsvChange}
                  />
                  <button
                    type="button"
                    onClick={handleDownloadClosingStock}
                    disabled={isDownloadingCsv || !selectedProductIds.length}
                    className="inline-flex h-6 w-6 items-center justify-center rounded text-brand bg-[#F7E5D5] disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Download closing stock CSV"
                    title="Download closing stock CSV"
                  >
                    <FiDownload className="h-4 w-4" strokeWidth={1.6} />
                  </button>
                  <button
                    type="button"
                    onClick={handleUploadClosingStock}
                    disabled={isImportingCsv}
                    className="inline-flex h-6 w-6 items-center justify-center rounded text-brand bg-[#F7E5D5] disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Upload closing stock CSV"
                    title="Upload closing stock CSV"
                  >
                    <FiUpload className="h-4 w-4" strokeWidth={1.6} />
                  </button>
                  <button
                    type="button"
                    onClick={toggleAllProducts}
                    className="text-xs font-medium text-brand hover:underline"
                  >
                    {selectedProductCount === catalogItems.length
                      ? 'Deselect All'
                      : 'Select All'}
                  </button>
                </div>
              ) : null}
            </div>
            <input
              type="text"
              placeholder="Search sku name"
              value={itemSearch}
              onChange={(e) => setItemSearch(e.target.value)}
              className="mb-3 w-full rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
            {selectedCategories.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500">
                Select a category to see sku name.
              </p>
            ) : productsBusy && catalogItems.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500">Loading SKUs…</p>
            ) : visibleItems.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500">
                {itemSearch.trim() ? 'No SKUs match your search.' : 'No SKUs found.'}
              </p>
            ) : (
              <ul className="max-h-72 flex-1 space-y-1 overflow-y-auto">
                {visibleItems.map((item) => {
                  const checked = isProductChecked(item);
                  return (
                    <li key={item.id} className="flex items-start gap-1.5 rounded-lg px-2 py-1.5 hover:bg-gray-50">
                      <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-2.5 text-sm">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleProduct(item)}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand accent-brand"
                        />
                        <span className="min-w-0 flex-1">
                          <span className={`block truncate ${checked ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                            {item.itemName}
                          </span>
                          <span className="block truncate text-xs text-gray-500">
                            {item.itemCode} · {item.subCategory || item.category}
                          </span>
                        </span>
                      </label>
                      {checked ? (
                        <PlanningEditButton onClick={() => openSkuPlanning(item)} />
                      ) : null}
                  </li>
                );
              })}
            </ul>
            )}
          </div>
          </div>

        <div className="mt-6 min-w-0">
          <HistoricalAverageSalesTable
            items={mappedProducts}
            weeklyHistory={weeklyHistory}
            loading={dailySalesBusy}
            emptyMessage={
              !hasWeeks || !wizardData.toolDate
                ? 'Select date and sales history weeks to see historical sales.'
                : 'No historical sales found for this date.'
            }
            hint={
              dailySalesError
                ? 'Could not load Daily Sales for this date.'
                : 'Qty, UPT, and revenue come only from Daily Sales. SKUs without API history are hidden.'
            }
          />
        </div>

        <div className="mt-6 rounded-xl border border-gray-200 bg-white p-5">
          <h4 className="mb-1 text-sm font-semibold text-gray-800">Actual GC &amp; Sales</h4>
          <div className='my-1'>
          <FormulaInfoHint
            title="Actual GC & Sales — formulas"
            items={ACTUAL_GC_SALES_FORMULAS}
          />
          </div>
          {dailySalesBusy ? (
            <p className="mb-4 text-sm text-gray-500">Loading Daily Sales…</p>
          ) : !apiDailySalesRows.length ? (
            <p className="mb-4 text-sm text-amber-700">
              No Daily Sales found for this selection. Actual GC and sales stay 0 until data is
              available.
            </p>
          ) : null}
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700"> </th>
                  <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">W-1</th>
                  <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">W-2</th>
                  <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">W-3</th>
                  <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">AVG</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border-b border-gray-100 px-3 py-2 font-medium text-gray-600">Date / Code</td>
                  {['week1Label', 'week2Label', 'week3Label'].map((key) => (
                    <td key={key} className="border-b border-gray-100 px-3 py-2">
                      <input
                        type="text"
                        readOnly
                        tabIndex={-1}
                        value={params[key] ?? ''}
                        className={actualGcCellClasses}
                        aria-readonly="true"
                      />
                    </td>
                  ))}
                  <td className="border-b border-gray-100 px-3 py-2 text-sm text-gray-400">—</td>
                </tr>
                <tr>
                  <td className="border-b border-gray-100 px-3 py-2 font-medium text-gray-600">
                    Sales <span className="text-red-600">*</span>
                  </td>
                  {['week1Sales', 'week2Sales', 'week3Sales'].map((key) => (
                    <td key={key} className="border-b border-gray-100 px-3 py-2">
                      <input
                        type="text"
                        readOnly
                        tabIndex={-1}
                        value={params[key] ?? ''}
                        className={actualGcCellClasses}
                        aria-readonly="true"
                      />
                    </td>
                  ))}
                  <td className="border-b border-gray-100 px-3 py-2 text-sm font-semibold text-gray-900">
                    {formatInt(avgSales)}
                  </td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-medium text-gray-600">
                    GC <span className="text-red-600">*</span>
                  </td>
                  {['week1Gc', 'week2Gc', 'week3Gc'].map((key) => (
                    <td key={key} className="px-3 py-2">
                      <input
                        type="text"
                        readOnly
                        tabIndex={-1}
                        value={params[key] ?? ''}
                        className={actualGcCellClasses}
                        aria-readonly="true"
                      />
                    </td>
                  ))}
                  <td className="px-3 py-2 text-sm font-semibold text-gray-900">{formatInt(avgGc)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        
        </div>
      </fieldset>
      {planningEdit ? (
        <PlanningPopup
          title={planningEdit.title}
          subtitle={planningEdit.subtitle}
          values={planningEdit.values}
          variant={planningEdit.type}
          onChange={(values) => setPlanningEdit((prev) => ({ ...prev, values }))}
          onClose={() => setPlanningEdit(null)}
          onSave={savePlanningEdit}
        />
      ) : null}
    </div>
  );
}

export default StepConfigureTool;
